import { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography
} from '@mui/material';
import { Close, MyLocation, PinDrop, RestartAlt } from '@mui/icons-material';
import { GoogleMap, Marker } from '@react-google-maps/api';
import { useTheme } from '@mui/material/styles';
import { useDispatch, useSelector } from 'react-redux';
import { Scooter } from '../../resources/types/scootersTypes';
import { updateScooterInfoAsync, selectScooters } from '../../redux/slices/scootersSlice';
import { selectParkings } from '../../redux/slices/parkingsSlice';
import ParkingMarker from '../../components/ParkingMarker';
import { getFilledSquareMarkerIcon } from '../../helpers/scooter-markers-helper';

interface Props {
  open: boolean;
  onClose: () => void;
  scooter?: Scooter;
}

const mapContainerStyle = { width: '100%', height: '500px' };

const defaultCenter = { lat: 48.9225224, lng: 24.7103188 };

const ForceLocationModal = ({ open, onClose, scooter }: Props) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const [picked, setPicked] = useState<{ lat: number; lon: number } | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const scootersState = useSelector(selectScooters);
  const parkingsState = useSelector(selectParkings);

  useEffect(() => {
    if (!open) {
      setPicked(null);
      setSubmitting(false);
    }
  }, [open]);

  const center = scooter?.coords?.lat && scooter?.coords?.lon
    ? { lat: scooter.coords.lat, lng: scooter.coords.lon }
    : defaultCenter;

  // Один раз центруємо мапу при відкритті — далі не контролюємо center,
  // щоб користувач міг вільно пересувати карту.
  const mapInstanceRef = useRef<google.maps.Map | null>(null);
  const centeredForRef = useRef<string | null>(null);

  const handleMapLoad = useCallback((map: google.maps.Map) => {
    mapInstanceRef.current = map;
    if (scooter?._id && centeredForRef.current !== scooter._id) {
      map.setCenter(center);
      map.setZoom(16);
      centeredForRef.current = scooter._id;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scooter?._id]);

  useEffect(() => {
    if (open && scooter?._id && mapInstanceRef.current && centeredForRef.current !== scooter._id) {
      mapInstanceRef.current.setCenter(center);
      mapInstanceRef.current.setZoom(16);
      centeredForRef.current = scooter._id;
    }
    if (!open) {
      centeredForRef.current = null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, scooter?._id]);

  const handleMapClick = useCallback((e: google.maps.MapMouseEvent) => {
    if (!e.latLng) return;
    setPicked({ lat: e.latLng.lat(), lon: e.latLng.lng() });
  }, []);

  const useMyLocation = () => {
    if (!navigator.geolocation) {
      window.alert('Геолокація недоступна у вашому браузері');
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => setPicked({ lat: pos.coords.latitude, lon: pos.coords.longitude }),
      () => window.alert('Не вдалося отримати вашу локацію')
    );
  };

  const handleSetLocation = async () => {
    if (!scooter || !picked) return;
    if (!window.confirm(`Встановити примусову локацію самокату ${scooter.name}?`)) return;
    setSubmitting(true);
    await new Promise<void>((resolve) => {
      dispatch(
        updateScooterInfoAsync(
          scooter._id,
          {
            forceCoords: { lat: picked.lat, lon: picked.lon }
          } as any,
          () => resolve()
        ) as any
      );
      setTimeout(resolve, 5000);
    });
    setSubmitting(false);
    onClose();
  };

  const handleResetLocation = async () => {
    if (!scooter) return;
    if (!window.confirm('Скинути примусову локацію? Самокат знову показуватиме реальні GPS-координати.')) return;
    setSubmitting(true);
    await new Promise<void>((resolve) => {
      dispatch(
        updateScooterInfoAsync(
          scooter._id,
          { forceCoords: null } as any,
          () => resolve()
        ) as any
      );
      setTimeout(resolve, 5000);
    });
    setSubmitting(false);
    onClose();
  };

  const realCoords = scooter?.trackerCoords && scooter.trackerCoords.lat
    ? { lat: scooter.trackerCoords.lat, lng: scooter.trackerCoords.lon }
    : scooter?.coords && scooter.coords.lat
      ? { lat: scooter.coords.lat, lng: scooter.coords.lon }
      : null;

  const mapOptions: google.maps.MapOptions = {
    streetViewControl: false,
    mapTypeControl: false,
    fullscreenControl: false
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PinDrop sx={{ color: '#4ECDC4' }} />
          <Typography variant="h6">
            Примусова локація — Самокат № {scooter?.name}
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {!picked && (
          <Typography variant="body2" sx={{ mb: 1, color: theme.palette.text.secondary }}>
            Натисніть на карту, щоб поставити точку
          </Typography>
        )}

        <Box sx={{ position: 'relative', mb: 2 }}>
          {typeof window !== 'undefined' && window.google?.maps ? (
            <GoogleMap
              mapContainerStyle={mapContainerStyle}
              options={mapOptions}
              onClick={handleMapClick}
              onLoad={handleMapLoad}
              onUnmount={() => { mapInstanceRef.current = null; }}
            >
              {parkingsState?.parkings?.map(parking => (
                <ParkingMarker key={parking._id} parking={parking} />
              ))}

              {scootersState?.items?.map((s) => {
                if (!s.coords || s._id === scooter?._id) return null;
                const icon = getFilledSquareMarkerIcon(s, s.name);
                if (!icon) return null;
                return (
                  <Marker
                    key={s._id}
                    position={{ lat: s.coords.lat, lng: s.coords.lon }}
                    icon={{
                      url: icon,
                      labelOrigin: new window.google.maps.Point(16, 16),
                      scaledSize: new window.google.maps.Size(28, 28)
                    }}
                    label={{
                      text: String(s.battery ?? '').replace(/^[*!]+/, '').replace('24h ', '') || ' ',
                      fontSize: '10px',
                      fontWeight: '600',
                      color: '#FFFFFF'
                    }}
                    opacity={0.85}
                    zIndex={1}
                    clickable={false}
                  />
                );
              })}

              {realCoords && (
                <Marker
                  position={realCoords}
                  icon={{
                    path: window.google.maps.SymbolPath.CIRCLE,
                    scale: 9,
                    fillColor: '#2E7D32',
                    fillOpacity: 1,
                    strokeColor: '#FFFFFF',
                    strokeWeight: 2
                  }}
                  title="Реальна локація (GPS-трекер)"
                />
              )}
              {picked && (
                <Marker
                  position={{ lat: picked.lat, lng: picked.lon }}
                  icon={{
                    path: window.google.maps.SymbolPath.CIRCLE,
                    scale: 9,
                    fillColor: '#D32F2F',
                    fillOpacity: 1,
                    strokeColor: '#FFFFFF',
                    strokeWeight: 2
                  }}
                  title="Нова примусова локація"
                  zIndex={10000}
                />
              )}
            </GoogleMap>
          ) : (
            <Box sx={{
              ...mapContainerStyle,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: theme.palette.mode === 'light' ? '#F5F5F5' : '#2A2A2A'
            }}>
              <CircularProgress />
            </Box>
          )}
        </Box>

        <Stack direction="row" spacing={2} sx={{ mb: 1, flexWrap: 'wrap', gap: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#2E7D32', border: '2px solid #FFF', boxShadow: '0 0 0 1px #999' }} />
            <Typography variant="caption">Реальна (GPS)</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#D32F2F', border: '2px solid #FFF', boxShadow: '0 0 0 1px #999' }} />
            <Typography variant="caption">Нова обрана</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ width: 12, height: 12, borderRadius: '3px', bgcolor: '#4ECDC4', border: '2px solid #FFF', boxShadow: '0 0 0 1px #999' }} />
            <Typography variant="caption">Готелі</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ width: 12, height: 12, borderRadius: '3px', bgcolor: '#888', border: '2px solid #FFF', boxShadow: '0 0 0 1px #999' }} />
            <Typography variant="caption">Інші самокати</Typography>
          </Box>
        </Stack>

        {picked && (
          <Box sx={{
            p: 1.5,
            borderRadius: '8px',
            bgcolor: theme.palette.mode === 'light' ? '#F5F5F5' : '#2A2A2A',
            mb: 1
          }}>
            <Typography variant="body2">
              <strong>Обрані координати:</strong> {picked.lat.toFixed(6)}, {picked.lon.toFixed(6)}
            </Typography>
          </Box>
        )}

        <Button
          startIcon={<MyLocation />}
          onClick={useMyLocation}
          variant="outlined"
          size="small"
          sx={{
            borderColor: '#4ECDC4',
            color: '#4ECDC4',
            '&:hover': { borderColor: '#3BA99F', bgcolor: 'rgba(78,205,196,0.08)' }
          }}
        >
          Використати мою локацію
        </Button>
      </DialogContent>

      <DialogActions sx={{ p: 2, justifyContent: 'space-between' }}>
        <Button
          startIcon={<RestartAlt />}
          onClick={handleResetLocation}
          disabled={submitting}
          color="error"
          variant="outlined"
        >
          Скинути примусову локацію
        </Button>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button onClick={onClose} disabled={submitting}>
            Скасувати
          </Button>
          <Button
            onClick={handleSetLocation}
            disabled={!picked || submitting}
            variant="contained"
            sx={{
              bgcolor: '#4ECDC4',
              '&:hover': { bgcolor: '#3BA99F' }
            }}
            startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : <PinDrop />}
          >
            Встановити локацію
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default ForceLocationModal;
